// BarChart/index.tsx

import * as utils from '@lib/utils';
import React, { useEffect } from 'react';
import {
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import Animated, {
    Easing,
    SharedValue,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import Svg, { Line } from 'react-native-svg';

import { ChartDataPointProps } from '@/interfaces/chart.interface';
import importedStyles from './../styles';

interface GiftedBarChartProps {
    data: ChartDataPointProps[];
    height?: number;
    barWidth?: number;
    barGap?: number;
    spacing?: number;
    initialSpacing?: number;
    endSpacing?: number;
    _inColor?: string;
    _outColor?: string;
    noOfSections?: number;
    rulesColor?: string;
    rulesType?: 'solid' | 'dashed';
    animationDuration?: number;
}

// 1. EXTRACT INTO A SEPARATE INNER COMPONENT TO HANDLE HOOKS SAFELY
interface BarItemProps {
    item: ChartDataPointProps;
    index: number;
    isLast: boolean;
    groupWidth: number;
    spacing: number;
    barWidth: number;
    barGap: number;
    _inColor: string;
    _outColor: string;
    maxVal: number;
    chartHeight: number;
    animationProgress: SharedValue<number>;
    isSelected: boolean;
    onPressLabel: (item: ChartDataPointProps, index: number) => void;
}

const BarItem: React.FC<BarItemProps> = ({
    item,
    index,
    isLast,
    groupWidth,
    spacing,
    barWidth,
    barGap,
    _inColor,
    _outColor,
    maxVal,
    chartHeight,
    animationProgress,
    isSelected,
    onPressLabel,
}) => {
    // Hooks are now safely called at the top level of this individual item component
    const animatedInStyle = useAnimatedStyle(() => ({
        height: (item.in / maxVal) * chartHeight * animationProgress.value,
    }));

    const animatedOutStyle = useAnimatedStyle(() => ({
        height: (item.out / maxVal) * chartHeight * animationProgress.value,
    }));

    return (
        <View
            style={[
                importedStyles.columnGroup,
                { width: groupWidth, marginRight: isLast ? 0 : spacing },
            ]}>
            <View style={[importedStyles.barsWrapper, { gap: barGap }]}>
                <Animated.View
                    style={[
                        importedStyles.bar,
                        { width: barWidth, backgroundColor: _inColor },
                        animatedInStyle,
                    ]}
                />
                <Animated.View
                    style={[
                        importedStyles.bar,
                        { width: barWidth, backgroundColor: _outColor },
                        animatedOutStyle,
                    ]}
                />
            </View>

            {/* Interactive Button Label Container */}
            <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => onPressLabel(item, index)}
                style={[
                    importedStyles.labelTouchable,
                    isSelected ? importedStyles.touchableLabelSelected : {},
                ]}>
                <Text style={importedStyles.labelText}>{item.label}</Text>
            </TouchableOpacity>
        </View>
    );
};

// 2. MAIN COMPONENT
export const GiftedBarChart: React.FC<GiftedBarChartProps> = ({
    data,
    height = 180,
    barWidth = 19,
    barGap = 6,
    spacing = 25,
    initialSpacing = 20,
    endSpacing = 20,
    _inColor = '#22c55e',
    _outColor = '#3b82f6',
    noOfSections = 3,
    rulesColor = '#e2e8f0',
    rulesType = 'dashed',
    animationDuration = 1200 * 2,
}) => {

    const [selectedPointDataIndex, setSelectedPointDataIndex] = React.useState<number | null>(null);

    React.useEffect(() => {
        const defaultIndex = data.length ? data.length - 1 : null;
        setSelectedPointDataIndex(defaultIndex);
    }, [data]);

    const onPressLabel = (point: ChartDataPointProps, index: number) => {
        setSelectedPointDataIndex(index);
    };

    const scrollViewRef = React.useRef<null | ScrollView>(null);

    React.useEffect(() => {
        if (scrollViewRef?.current) {
            scrollViewRef.current?.scrollToEnd({ animated: false });
        }
    }, [scrollViewRef]);

    const selectedPoint = (selectedPointDataIndex != null) ? data[selectedPointDataIndex] : null;
    const selectedPointIn = (selectedPoint != null) ? utils.printDecimalOf(selectedPoint.in) : "---";
    const selectedPointOut = (selectedPoint != null) ? utils.printDecimalOf(selectedPoint.out) : "---";
    const selectedPointDelta = (selectedPoint != null) ? utils.printDecimalOf(selectedPoint.in - selectedPoint.out) : "---";

    const paddingBottom = 35;
    const chartHeight = height - paddingBottom;
    const maxVal = Math.max(...data.flatMap((d) => [d.in, d.out]), 1) * 1.1;

    const groupWidth = barWidth * 2 + barGap;
    const totalContentWidth =
        initialSpacing +
        endSpacing +
        data.length * groupWidth +
        (data.length - 1) * spacing;

    const animationProgress = useSharedValue(0);

    useEffect(() => {
        animationProgress.value = withTiming(1, {
            duration: animationDuration,
            easing: Easing.out(Easing.cubic),
        });
    }, [data, animationDuration, animationProgress]);

    const sections = Array.from({ length: noOfSections + 1 });

    return (
        <View style={importedStyles.viewport}>

            {/* LEGENDS */}
            <View>
                {/* --- HEADER SUMMARY --- */}
                <View style={importedStyles.headerRow}>
                    <View>
                        <Text style={importedStyles.headerTitle}>
                            Net $ {selectedPointDelta}
                        </Text>
                    </View>
                </View>

                {/* INCOME */}
                <View key={0} style={importedStyles.listRow}>
                    <View style={importedStyles.leftColumn}>
                        <View style={[importedStyles.bullet, { backgroundColor: "#22c55e" }]} />
                        <Text style={importedStyles.categoryName}>Income</Text>
                    </View>
                    <View style={importedStyles.rightColumn}>
                        <Text style={importedStyles.categoryPercentage}>$ {selectedPointIn}</Text>
                        <Text style={importedStyles.categoryValue}></Text>
                    </View>
                </View>

                {/* EXPENSES */}
                <View key={1} style={importedStyles.listRow}>
                    <View style={importedStyles.leftColumn}>
                        <View style={[importedStyles.bullet, { backgroundColor: "#3b82f6" }]} />
                        <Text style={importedStyles.categoryName}>Expenses</Text>
                    </View>
                    <View style={importedStyles.rightColumn}>
                        <Text style={importedStyles.categoryPercentage}>$ {selectedPointOut}</Text>
                        <Text style={importedStyles.categoryValue}></Text>
                    </View>
                </View>
            </View>

            <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                ref={scrollViewRef}>
                <View style={[importedStyles.chartContainer, { height, width: totalContentWidth }]}>

                    {/* Static Absolute Position Grid */}
                    <Svg width={totalContentWidth} height={chartHeight} style={StyleSheet.absoluteFill}>
                        {sections.map((_, i) => {
                            const yPos = 10 + (i / noOfSections) * (chartHeight - 20);
                            return (
                                <Line
                                    key={i}
                                    x1={0}
                                    y1={yPos}
                                    x2={totalContentWidth}
                                    y2={yPos}
                                    stroke={rulesColor}
                                    strokeWidth={1}
                                    strokeDasharray={rulesType === 'dashed' ? '5,5' : undefined}
                                />
                            );
                        })}
                    </Svg>

                    {/* Paint chart bars and labels */}
                    <View style={[importedStyles.barsContainer, { paddingLeft: initialSpacing, paddingRight: endSpacing }]}>
                        {data?.map((item, index) => (
                            <BarItem
                                key={index}
                                item={item}
                                index={index}
                                isLast={index === data.length - 1}
                                groupWidth={groupWidth}
                                spacing={spacing}
                                barWidth={barWidth}
                                barGap={barGap}
                                _inColor={_inColor}
                                _outColor={_outColor}
                                maxVal={maxVal}
                                chartHeight={chartHeight}
                                animationProgress={animationProgress}
                                isSelected={index === selectedPointDataIndex}
                                onPressLabel={onPressLabel}
                            />
                        ))}
                    </View>

                </View>
            </ScrollView>
        </View>
    );
};

export default GiftedBarChart;